import { useState, useEffect } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers } from "@fortawesome/free-solid-svg-icons";

interface GospelItem {
  letter: string;
  english: string;
  korean: string;
  description: string;
}

const gospelItems: GospelItem[] = [
  {
    letter: "G",
    english: "Glorifying",
    korean: "복음을 영화롭게 하는 교회",
    description: "복음공동체",
  },
  {
    letter: "O",
    english: "Outreaching",
    korean: "땅끝까지 선교하는 교회",
    description: "선교공동체",
  },
  {
    letter: "S",
    english: "Standing",
    korean: "말씀 앞에서 기도하는 교회",
    description: "말씀공동체",
  },
  {
    letter: "P",
    english: "Preparing",
    korean: "다음 세대를 준비하는 교회",
    description: "이음공동체",
  },
  {
    letter: "E",
    english: "Equipping",
    korean: "예수님의 제자를 훈련하는 교회",
    description: "제자공동체",
  },
  {
    letter: "L",
    english: "Loving",
    korean: "하나님과 이웃을 사랑하는 교회",
    description: "사랑공동체",
  },
];

const AboutSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideHeight, setSlideHeight] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const updateHeight = () => {
      const activeSlide = document.querySelector(".about-slide.active");
      if (activeSlide) {
        const content = activeSlide.querySelector(".about-content");
        if (content) {
          const contentHeight = content.scrollHeight;
          setSlideHeight(contentHeight + 40);
        }
      }
    };

    const timer = setTimeout(updateHeight, 100);
    window.addEventListener("resize", updateHeight);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateHeight);
    };
  }, [currentSlide]);

  return (
    <Section className="about-section" id="about">
      <Container className="container">
        <SectionHeader className="section-header">
          <SectionTitle className="section-title">
            <FontAwesomeIcon icon={faUsers} className="section-icon" /> 교회
            소개
          </SectionTitle>
          <SectionTitleEng className="section-title-eng">
            WHO WE ARE?
          </SectionTitleEng>
          <SectionDesc className="section-desc">
            처음사랑교회, 우리는 누구인가?
          </SectionDesc>
        </SectionHeader>

        <SliderContainer
          className="about-slider-container"
          style={{ height: slideHeight }}
        >
          {/* Mission Slide */}
          <Slide
            className={`about-slide ${currentSlide === 0 ? "active" : ""}`}
          >
            <SlideContent className="about-content">
              <SubTitle className="about-sub-title">OUR MISSION</SubTitle>
              <MainTitle className="about-main-title">우리의 사명</MainTitle>
              <Quote className="about-quote">
                "처음사랑교회는 십자가의 복음을 깊이 알고,
                <br />
                온전히 누리며, 삶으로 전하는 공동체입니다."
              </Quote>
              <Text className="about-text">
                우리는 먼저 복음을 듣고, 믿고, 누리며, 삶으로 살아내기 위해
                존재합니다. 동시에 아직 복음을 알지 못하는 이들에게 복음을
                전하고, 그들 역시 복음을 듣고, 믿고, 누리며, 삶으로 살아내도록
                돕기 위해 존재합니다.
              </Text>
            </SlideContent>
          </Slide>

          {/* Vision Slide */}
          <Slide
            className={`about-slide ${currentSlide === 1 ? "active" : ""}`}
          >
            <SlideContent className="about-content">
              <SubTitle className="about-sub-title">OUR VISION</SubTitle>
              <MainTitle className="about-main-title">우리의 비전</MainTitle>
              <Quote className="about-quote">
                "예수님이 다시 오실 때까지
                <br />
                복음을 영화롭게 하는 거룩한 교회로 존재하는 것입니다."
              </Quote>
              <Text className="about-text">
                우리에게 중요한 것은 우리가 무엇을 이루느냐가 아니라, 어떤
                교회로 그분 앞에 서느냐 하는 것입니다.
              </Text>
            </SlideContent>
          </Slide>

          {/* Core Values Slide */}
          <Slide
            className={`about-slide ${currentSlide === 2 ? "active" : ""}`}
          >
            <SlideContent className="about-content">
              <SubTitle className="about-sub-title">OUR CORE VALUES</SubTitle>
              <MainTitle className="about-main-title">
                핵심 가치 (GOSPEL)
              </MainTitle>
              <Quote
                className="about-quote"
                style={{ fontSize: "18px", marginBottom: "15px" }}
              >
                "우리의 핵심 가치는 복음(GOSPEL)입니다."
              </Quote>
              <GospelGrid className="gospel-grid">
                {gospelItems.map((item) => (
                  <GospelCard key={item.letter} className="gospel-card">
                    <GcHeader className="gc-header">
                      <GcInitial className="gc-initial">
                        {item.letter}
                      </GcInitial>
                      <GcEng className="gc-eng">{item.english}</GcEng>
                    </GcHeader>
                    <GcKor className="gc-kor">{item.korean}</GcKor>
                    <GcDesc className="gc-desc">{item.description}</GcDesc>
                  </GospelCard>
                ))}
              </GospelGrid>
            </SlideContent>
          </Slide>
        </SliderContainer>

        <DotsContainer className="about-dots">
          <Dot
            className={`about-dot ${currentSlide === 0 ? "active" : ""}`}
            onClick={() => setCurrentSlide(0)}
          />
          <Dot
            className={`about-dot ${currentSlide === 1 ? "active" : ""}`}
            onClick={() => setCurrentSlide(1)}
          />
          <Dot
            className={`about-dot ${currentSlide === 2 ? "active" : ""}`}
            onClick={() => setCurrentSlide(2)}
          />
        </DotsContainer>

        <KaicamInfo className="kaicam-info">
          <KaicamTitle className="kaicam-title">
            처음사랑교회는 사단법인 한국독립교회선교단체연합회(KAICAM)에 소속된
            교회입니다.
          </KaicamTitle>
          <KaicamText className="kaicam-text">
            할렐루야교회, 새순교회, 갈보리교회, 우리들교회, 새로운교회,
            베이직교회 등이 함께 하고 있습니다.
          </KaicamText>
        </KaicamInfo>
      </Container>
    </Section>
  );
};

const Section = styled.section`
  background: #f9f9f9;
  padding: 80px 0 100px 0;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 60px 0 80px 0;
  }

  @media (max-width: 480px) {
    padding: 40px 0 60px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }

  @media (max-width: 480px) {
    padding: 0 10px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const SectionTitle = styled.h2`
  font-size: 32px;
  color: #004094;
  margin-bottom: 5px;
  letter-spacing: -1px;
  line-height: 1.2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 700;

  .section-icon {
    font-size: 0.9em;
    color: #004094;
  }

  @media (max-width: 768px) {
    font-size: 24px;
    flex-direction: column;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 20px;
    gap: 5px;
  }
`;

const SectionTitleEng = styled.span`
  font-size: 14px;
  color: #999;
  display: block;
  margin-bottom: 20px;
  letter-spacing: 2px;
  text-transform: uppercase;
  font-weight: 700;
`;

const SectionDesc = styled.p`
  font-size: 18px;
  color: #555;
  font-weight: 300;
  word-break: keep-all;
`;

const SliderContainer = styled.div`
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
  transition: height 0.3s ease-in-out;
`;

const Slide = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 20px 0;
  opacity: 0;
  transition: opacity 0.8s ease-in-out;
  pointer-events: none;
  overflow-y: auto;

  &.active {
    opacity: 1;
    pointer-events: auto;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 15px 0;
  }

  @media (max-width: 480px) {
    padding: 10px 0;
  }
`;

const SlideContent = styled.div`
  text-align: center;
  width: 100%;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const SubTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #999;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 10px;
`;

const MainTitle = styled.h3`
  font-size: 36px;
  color: #004094;
  margin-bottom: 30px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 28px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const Quote = styled.p`
  font-size: 22px;
  font-weight: 700;
  color: #222;
  margin-bottom: 20px;
  line-height: 1.4;
  word-break: keep-all;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const Text = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #555;
  font-weight: 300;
  word-break: keep-all;
  max-width: 700px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 14px;
    max-width: 90%;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 1.6;
  }
`;

const GospelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-top: 10px;
  text-align: left;
  max-width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    margin-top: 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
    margin-top: 10px;
  }
`;

const GospelCard = styled.div`
  background: white;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  min-height: 120px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 12px;
    min-height: 80px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    min-height: 70px;
  }
`;

const GcHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const GcInitial = styled.span`
  font-size: 24px;
  font-weight: 900;
  color: #004094;
`;

const GcEng = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
`;

const GcKor = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #333;
  line-height: 1.4;
  word-break: keep-all;

  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 1.3;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 1.2;
  }
`;

const GcDesc = styled.div`
  font-size: 13px;
  color: #666;
  margin-top: 5px;
  font-weight: 300;
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  position: relative;
  z-index: 10;
`;

const Dot = styled.div`
  width: 12px;
  height: 12px;
  background: #ddd;
  border-radius: 50%;
  cursor: pointer;
  transition: 0.3s;

  &.active {
    background: #004094;
    transform: scale(1.2);
  }
`;

const KaicamInfo = styled.div`
  max-width: 800px;
  margin: 60px auto 0;
  text-align: center;
  padding-top: 40px;
  border-top: 1px solid #e0e0e0;
`;

const KaicamTitle = styled.div`
  font-size: 18px;
  color: #004094;
  margin-bottom: 10px;
  font-weight: 700;
`;

const KaicamText = styled.p`
  font-size: 15px;
  color: #666;
  font-weight: 300;
  line-height: 1.6;
  word-break: keep-all;
`;

export default AboutSection;
